import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Collapse,
  Drawer,
  Grid,
  Empty,
  Input,
  Modal,
  Popconfirm,
  Select,
  Spin,
  Tabs,
  Tag,
  Upload,
} from "antd";
import {
  ArrowLeftOutlined,
  FileTextOutlined,
  MoreOutlined,
  ReloadOutlined,
  SafetyOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import DocumentIntakeIntro, { DocumentIntakeNewBadge } from "./DocumentIntakeIntro";
import OriginalDocument from "./OriginalDocument";
import FieldInput, { displayValue, fieldLabel } from "./FieldInput";
import "./sourceDocuments.css";
import type { UploadFile } from "antd/es/upload/interface";
import { useGlobalContext } from "../../store/GlobalContext";
import api from "../../services/api";
import { canManageEstate } from "../../services/access-policy";
import {
  ExtractionProposal,
  RecordComparison,
  ReviewAction,
  SourceDocument,
  sourceDocumentsService as service,
} from "./sourceDocuments.service";

const targetLabels: Record<string, string> = {
  owner: "Deceased person",
  asset: "Asset",
  liability: "Liability",
  participant: "Participant",
  estateTax: "Estate tax",
  documentAnswers: "Probate filing details",
  estate: "Estate details",
  creditor: "Creditor",
  cashflow: "Cash flow",
};
const actionable = (proposal: ExtractionProposal) =>
  ["pending", "unverified", "deferred"].includes(proposal.status);
const errorMessage = (error: any) => {
  const detail = error.response?.data?.message;
  return Array.isArray(detail)
    ? detail.join(", ")
    : detail || error.message || "The request failed. Please try again.";
};

export default function SourceDocumentsPage() {
  const screens = Grid.useBreakpoint();
  const compactReview = screens.xl === false;
  const [mobileReviewView, setMobileReviewView] = useState("information");
  const { selectedEstate, auth } = useGlobalContext();
  const estateId = selectedEstate?.uuid;
  const activeEstate = useRef(estateId);
  activeEstate.current = estateId;
  const [catalog, setCatalog] = useState<{
    targets: Record<
      string,
      { key: string; required?: boolean; validators?: unknown }[]
    >;
    documents: {
      uid: string;
      name: string;
      fields: { key: string; label?: string }[];
    }[];
    participants: { uid: string; name: string }[];
  }>({ targets: {}, documents: [], participants: [] });
  const [documents, setDocuments] = useState<SourceDocument[]>([]);
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [extractingUids, setExtractingUids] = useState<string[]>([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [detailsUid, setDetailsUid] = useState<string>();
  const [detailsDescription, setDetailsDescription] = useState("");
  const [query, setQuery] = useState("");
  const [group, setGroup] = useState("all");
  const [recordId, setRecordId] = useState<string>();
  const [estateData, setEstateData] = useState<any>();
  const [currentValues, setCurrentValues] = useState<Record<string, unknown>>(
    {}
  );
  const [comparison, setComparison] = useState<RecordComparison>();
  const [comparisonRevision, setComparisonRevision] = useState(0);
  const [comparisonError, setComparisonError] = useState<string>();
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [keptFields, setKeptFields] = useState<string[]>([]);
  const [error, setError] = useState<string>();
  const [canConfirm, setCanConfirm] = useState(false);
  const [queue, setQueue] = useState("all");
  const [resultsUid, setResultsUid] = useState<string>();
  const [editing, setEditing] = useState<{
    document: SourceDocument;
    proposal: ExtractionProposal;
  }>();
  const [valuesText, setValuesText] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [editError, setEditError] = useState<string>();

  const refresh = useCallback(async () => {
    if (!estateId) return;
    const [data, estate] = await Promise.all([
      service.list(estateId),
      api.get(`/estates/${estateId}`),
    ]);
    if (activeEstate.current !== estateId) return;
    setDocuments(data);
    setEstateData(estate.data);
  }, [estateId]);
  useEffect(() => {
    let live = true;
    setDocuments([]);
    setCanConfirm(false);
    setEditing(undefined);
    setResultsUid(undefined);
    setError(undefined);
    if (!estateId) return;
    setLoading(true);
    Promise.all([
      service.list(estateId),
      api.get(`/estates/${estateId}`),
      api.get(`/source-documents/${estateId}/catalog`),
    ])
      .then(([data, estate, fields]) => {
        if (!live) return;
        setDocuments(data);
        setEstateData(estate.data);
        setCanConfirm(canManageEstate(estate.data, auth.user));
        setCatalog(fields.data);
      })
      .catch((err) => {
        if (live) setError(errorMessage(err));
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [estateId, auth.user]);

  const hasActiveExtraction = documents.some(
    (document) => document.status === "extracting"
  );
  useEffect(() => {
    if (!hasActiveExtraction) return;
    const timer = setInterval(() => {
      refresh().catch((err) => setError(errorMessage(err)));
    }, 4000);
    return () => clearInterval(timer);
  }, [hasActiveExtraction, refresh]);

  const run = async (operation: () => Promise<void>) => {
    setBusy(true);
    setError(undefined);
    try {
      await operation();
      await refresh();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const review = async (
    document: SourceDocument,
    proposal: ExtractionProposal,
    action: ReviewAction
  ) => {
    if (!estateId) return;
    await run(() => service.review(estateId, document.uid, proposal, action));
  };
  const confirm = async () => {
    if (
      !editing ||
      !estateId ||
      !confirmed ||
      !comparison ||
      comparisonLoading ||
      comparison.operation === "ambiguous"
    )
      return;
    let values: Record<string, unknown>;
    try {
      values = JSON.parse(valuesText);
      if (!values || Array.isArray(values) || typeof values !== "object")
        throw new Error("Use a JSON object containing field names and values.");
    } catch (err) {
      setEditError(errorMessage(err));
      return;
    }
    setBusy(true);
    setEditError(undefined);
    try {
      await service.review(
        estateId,
        editing.document.uid,
        {
          ...editing.proposal,
          recordUid:
            editing.proposal.recordUid === undefined
              ? comparison?.recordUid
              : editing.proposal.recordUid,
        },
        "confirm",
        values,
        comparison?.comparisonToken
      );
      setEditing(undefined);
      await refresh();
      const next = editing.document.proposals.find(
        (p) =>
          p.id !== editing.proposal.id &&
          actionable(p) &&
          (group === "all" || p.target === group)
      );
      setRecordId(next?.id);
    } catch (err) {
      setEditError(errorMessage(err));
      setConfirmed(false);
      setComparisonRevision((revision) => revision + 1);
    } finally {
      setBusy(false);
    }
  };

  let editableValues: Record<string, unknown> = {};
  try {
    const parsed = JSON.parse(valuesText);
    if (parsed && !Array.isArray(parsed) && typeof parsed === "object")
      editableValues = parsed;
  } catch {
    /* The advanced editor shows validation on confirmation. */
  }
  const reviewFields: any[] = editing
    ? editing.proposal.target === "documentAnswers"
      ? catalog.documents.find(
          (doc) => doc.uid === editing.proposal.documentUid
        )?.fields || []
      : catalog.targets[editing.proposal.target] || []
    : [];
  const requiredFields = reviewFields.filter(
    (field) =>
      field.required &&
      !(
        ["participant", "asset", "liability", "cashflow", "creditor"].includes(
          editing?.proposal.target || ""
        ) &&
        (editing?.proposal.recordUid || comparison?.recordUid)
      )
  );
  const fieldValues = {
    ...Object.fromEntries(
      requiredFields.map((field) => [field.key, undefined])
    ),
    ...editableValues,
  };
  const numericField = (key: string) =>
    (
      reviewFields.find((field) => field.key === key)?.validators as
        | { name?: string }[]
        | undefined
    )?.some((v) => ["isNumber", "isInt"].includes(v.name || ""));
  const setField = (key: string, value: unknown) => {
    setValuesText(JSON.stringify({ ...editableValues, [key]: value }, null, 2));
    setConfirmed(false);
  };

  const resultsDocument = documents.find(
    (document) => document.uid === resultsUid
  );
  const visibleProposals =
    resultsDocument?.proposals.filter(
      (p) => group === "all" || p.target === group
    ) || [];
  const selectedProposal =
    visibleProposals.find((p) => p.id === recordId) ||
    visibleProposals.find(actionable) ||
    visibleProposals[0];
  const dirty = Boolean(
    editing &&
      (valuesText !== JSON.stringify(editing.proposal.values, null, 2) ||
        editing.proposal.target !== selectedProposal?.target ||
        editing.proposal.recordUid !== selectedProposal?.recordUid ||
        editing.proposal.documentUid !== selectedProposal?.documentUid)
  );
  const navigate = (action: () => void) => {
    if (busy) return;
    if (dirty)
      Modal.confirm({
        title: "Discard unsaved review edits?",
        content:
          "Your source document and original extraction will be retained.",
        okText: "Discard edits",
        onOk: () => {
          setEditing(undefined);
          action();
        },
      });
    else {
      setEditing(undefined);
      action();
    }
  };
  useEffect(() => {
    setEditing(undefined);
    setConfirmed(false);
    setEditError(undefined);
    setKeptFields([]);
  }, [resultsUid, selectedProposal?.id]);
  useEffect(() => {
    let live = true;
    setCurrentValues({});
    setComparisonError(undefined);
    setComparisonLoading(false);
    if (!editing || !canConfirm || !estateId) return;
    setComparison(undefined);
    setConfirmed(false);
    setComparisonLoading(true);
    const timer = setTimeout(() => {
      let values: Record<string, unknown>;
      try {
        values = JSON.parse(valuesText);
      } catch {
        setComparisonLoading(false);
        setComparisonError("Enter valid field data before comparing.");
        return;
      }
      service
        .compare(estateId, editing.document.uid, editing.proposal, values)
        .then((result) => {
          if (!live) return;
          setComparison(result);
          setCurrentValues(result.currentValues);
          if (editing.proposal.recordUid === undefined && result.recordUid) {
            setEditing({
              ...editing,
              proposal: { ...editing.proposal, recordUid: result.recordUid },
            });
          }
        })
        .catch((err) => {
          if (live) setComparisonError(errorMessage(err));
        })
        .finally(() => {
          if (live) setComparisonLoading(false);
        });
    }, 250);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [editing, valuesText, estateId, canConfirm, comparisonRevision]);

  const startReview = () => {
    if (!resultsDocument || !selectedProposal) return;
    setEditing({
      document: resultsDocument,
      proposal: {
        ...selectedProposal,
        recordUid: selectedProposal.recordUid || undefined,
      },
    });
    setValuesText(JSON.stringify(selectedProposal.values, null, 2));
    setConfirmed(false);
    setEditError(undefined);
    setKeptFields([]);
  };
  const recordName = (proposal: ExtractionProposal) =>
    String(
      proposal.values.name ||
        [
          proposal.values.firstName,
          proposal.values.middleName,
          proposal.values.lastName,
        ]
          .filter(Boolean)
          .join(" ") ||
        targetLabels[proposal.target] ||
        proposal.target
    );
  const outstanding = (document: SourceDocument) =>
    document.proposals.filter(actionable).length;
  const state = (document: SourceDocument) =>
    extractingUids.includes(document.uid) || document.status === "extracting"
      ? "extracting"
      : document.status === "failed"
      ? "failed"
      : document.proposals.some(
          (p) => p.status === "applying" || p.status === "apply_failed"
        )
      ? "attention"
      : outstanding(document)
      ? document.proposals.some((p) => p.status === "pending")
        ? "review"
        : "deferred"
      : document.status === "extracted"
      ? document.proposals.length
        ? "completed"
        : "empty"
      : "ready";
  const stateLabels: Record<string, string> = {
    extracting: "Extracting",
    failed: "Extraction failed",
    review: "Needs review",
    deferred: "Saved for later",
    completed: "Completed",
    empty: "No relevant facts",
    ready: "Ready to extract",
    attention: "Needs attention",
  };
  const stateColors: Record<string, string> = {
    extracting: "processing",
    failed: "error",
    review: "gold",
    deferred: "purple",
    completed: "success",
    empty: "default",
    ready: "blue",
    attention: "error",
  };
  const filtered = documents.filter(
    (document) =>
      (queue === "all" ||
        (queue === "review"
          ? outstanding(document) > 0
          : state(document) === queue)) &&
      `${document.filename} ${document.documentType || ""} ${
        document.description || ""
      }`
        .toLowerCase()
        .includes(query.toLowerCase())
  );
  const extract = async (document: SourceDocument) => {
    if (!estateId || extractingUids.includes(document.uid)) return;
    setExtractingUids((old) => [...old, document.uid]);
    setError(undefined);
    try {
      await service.extract(estateId, document.uid);
      await refresh();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setExtractingUids((old) => old.filter((uid) => uid !== document.uid));
    }
  };
  const openDocument = (uid: string) =>
    navigate(() => {
      setResultsUid(uid);
      setRecordId(undefined);
      setGroup("all");
    });
  if (!estateId)
    return (
      <Alert
        type="info"
        message="Select an estate to upload and review its source documents."
      />
    );
  const privacy = (
    <>
      <p>
        Extraction sends this document and your description to OpenRouter and
        Google Vertex AI. Requests require zero retention routing, data
        collection disabled and no provider fallback.
      </p>
      <p>
        Uploading alone does not start extraction. Only authorized estate users
        can access uploads. You can enter data manually instead.
      </p>
    </>
  );

  return (
    <div className={`sd-page ${resultsDocument ? "sd-workspace" : ""}`}>
      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          closable
          onClose={() => setError(undefined)}
        />
      )}
      {!resultsDocument ? (
        <>
          <header className="sd-page-header">
            <div>
              <span className="sd-eyebrow">ESTATE DOCUMENTS</span>
              <h1>Uploaded documents <DocumentIntakeNewBadge /></h1>
              <p>
                Turn certificates, statements and court documents into
                information for your estate forms.
              </p>
            </div>
            <Button
              type="primary"
              size="large"
              icon={<UploadOutlined />}
              onClick={() => setUploadOpen(true)}
            >
              Upload documents
            </Button>
          </header>
          <DocumentIntakeIntro userUid={auth.user?.uid} />
          <div className="sd-summary">
            <div>
              <strong>{documents.length}</strong>
              <span>Documents</span>
            </div>
            <div>
              <strong>{documents.filter((d) => outstanding(d)).length}</strong>
              <span>Files to review</span>
            </div>
            <div>
              <strong>
                {documents.reduce((n, d) => n + outstanding(d), 0)}
              </strong>
              <span>Records awaiting review</span>
            </div>
            <Button
              type="text"
              icon={<SafetyOutlined />}
              onClick={() => setPrivacyOpen(true)}
            >
              Document privacy
            </Button>
          </div>
          <div className="sd-library-toolbar">
            <div
              className="sd-filters"
              role="group"
              aria-label="Document filters"
            >
              {[
                ["all", "All documents"],
                ["ready", "Ready to extract"],
                ["review", "Needs review"],
                ["deferred", "Saved for later"],
                ["completed", "Completed"],
                ["failed", "Failed"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={queue === key}
                  onClick={() => setQueue(key)}
                >
                  {label}
                  <span>
                    {
                      documents.filter(
                        (d) =>
                          key === "all" ||
                          (key === "review"
                            ? outstanding(d) > 0
                            : state(d) === key)
                      ).length
                    }
                  </span>
                </button>
              ))}
            </div>
            <div className="sd-search">
              <Input.Search
                aria-label="Search documents"
                placeholder="Search documents"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                allowClear
              />
              <Button
                aria-label="Refresh documents"
                icon={<ReloadOutlined />}
                loading={loading}
                onClick={() =>
                  refresh().catch((err) => setError(errorMessage(err)))
                }
              />
            </div>
          </div>
          <p className="sd-library-help">
            Extraction sends document content to OpenRouter and Google Vertex
            AI. Review the results before saving to estate forms.
          </p>
          <div className="sd-library">
            {loading ? (
              <div className="sd-loading">
                <Spin />
                <p>Loading your documents</p>
              </div>
            ) : !filtered.length ? (
              <Empty
                description={
                  documents.length
                    ? "No documents match these filters."
                    : "Your document library starts here."
                }
              >
                <Button
                  onClick={() =>
                    documents.length
                      ? (setQueue("all"), setQuery(""))
                      : setUploadOpen(true)
                  }
                >
                  {documents.length
                    ? "Reset filters"
                    : "Upload your first document"}
                </Button>
              </Empty>
            ) : (
              filtered.map((document) => (
                <article className="sd-file" key={document.uid}>
                  <div className="sd-file-icon">
                    <FileTextOutlined />
                  </div>
                  <div className="sd-file-info">
                    <button
                      className="sd-file-title"
                      title={document.filename}
                      onClick={() => openDocument(document.uid)}
                    >
                      {document.filename}
                    </button>
                    <div className="sd-file-meta">
                      <span>
                        {document.documentType ||
                          document.filename.split(".").pop()?.toUpperCase()}
                      </span>
                      <span>
                        {new Date(document.createdAt).toLocaleDateString(
                          undefined,
                          { month: "short", day: "numeric", year: "numeric" }
                        )}
                      </span>
                      {Boolean(outstanding(document)) && (
                        <span>{outstanding(document)} records to review</span>
                      )}
                    </div>
                    {document.description && <p>{document.description}</p>}
                    {state(document) === "extracting" && (
                      <div className="sd-progress" role="status">
                        <Spin size="small" />
                        <span>
                          Extracting information from this document...
                        </span>
                      </div>
                    )}
                    {document.error && (
                      <div className="sd-file-error" role="alert">
                        {document.error}
                      </div>
                    )}
                    {state(document) === "empty" && (
                      <p>
                        No relevant estate or probate information was found. No
                        estate records were changed.
                      </p>
                    )}
                  </div>
                  <div className="sd-file-actions">
                    <Tag color={stateColors[state(document)]}>
                      {stateLabels[state(document)]}
                    </Tag>
                    {["ready", "failed", "extracting"].includes(
                      state(document)
                    ) && !document.proposals.length ? (
                      <Button
                        type="primary"
                        loading={state(document) === "extracting"}
                        disabled={state(document) === "extracting"}
                        onClick={() => extract(document)}
                      >
                        {state(document) === "extracting"
                          ? "Extracting information"
                          : state(document) === "failed"
                          ? "Retry extraction"
                          : "Extract information"}
                      </Button>
                    ) : (
                      <Button
                        type={outstanding(document) ? "primary" : "default"}
                        onClick={() => openDocument(document.uid)}
                      >
                        {outstanding(document)
                          ? "Review information"
                          : "View document"}
                      </Button>
                    )}
                    <details className="sd-more">
                      <summary
                        aria-label={`More actions for ${document.filename}`}
                      >
                        <MoreOutlined />
                      </summary>
                      <div>
                        <Button
                          type="text"
                          onClick={() =>
                            service
                              .download(estateId, document)
                              .catch((err) => setError(errorMessage(err)))
                          }
                        >
                          Download original
                        </Button>
                        <Button
                          type="text"
                          onClick={() => {
                            setDetailsUid(document.uid);
                            setDetailsDescription(document.description || "");
                          }}
                        >
                          Edit description
                        </Button>
                        <Popconfirm
                          title="Delete this source file and proposals? Confirmed estate records are retained."
                          onConfirm={() =>
                            run(() => service.remove(estateId, document.uid))
                          }
                        >
                          <Button type="text" danger disabled={busy}>
                            Delete document
                          </Button>
                        </Popconfirm>
                      </div>
                    </details>
                  </div>
                </article>
              ))
            )}
          </div>
        </>
      ) : (
        <>
          <header className="sd-review-header">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(() => setResultsUid(undefined))}
            >
              Back to documents
            </Button>
            <div className="sd-file-navigation">
              <Button
                disabled={documents.indexOf(resultsDocument) === 0}
                onClick={() =>
                  openDocument(
                    documents[documents.indexOf(resultsDocument) - 1].uid
                  )
                }
              >
                Previous file
              </Button>
              <span>
                {documents.indexOf(resultsDocument) + 1} of {documents.length}
              </span>
              <Button
                disabled={
                  documents.indexOf(resultsDocument) === documents.length - 1
                }
                onClick={() =>
                  openDocument(
                    documents[documents.indexOf(resultsDocument) + 1].uid
                  )
                }
              >
                Next file
              </Button>
            </div>
          </header>
          <div className="sd-review-title">
            <div>
              <span className="sd-eyebrow">DOCUMENT REVIEW</span>
              <h1>{resultsDocument.documentType || "Document information"}</h1>
              <p title={resultsDocument.filename}>{resultsDocument.filename}</p>
            </div>
            <div>
              <Tag color={stateColors[state(resultsDocument)]}>
                {stateLabels[state(resultsDocument)]}
              </Tag>
              <p>
                {outstanding(resultsDocument)} of{" "}
                {resultsDocument.proposals.length} records awaiting review
              </p>
            </div>
          </div>
          {resultsDocument.error && (
            <Alert type="error" message={resultsDocument.error} />
          )}
          {compactReview && (
            <div
              className="sd-review-switch"
              role="group"
              aria-label="Review view"
            >
              <Button
                type={
                  mobileReviewView === "information" ? "primary" : "default"
                }
                onClick={() => setMobileReviewView("information")}
              >
                Extracted information
              </Button>
              <Button
                type={mobileReviewView === "original" ? "primary" : "default"}
                onClick={() => setMobileReviewView("original")}
              >
                Original document
              </Button>
            </div>
          )}
          <div
            className={`sd-review-grid ${
              compactReview ? "sd-review-grid-compact" : ""
            }`}
          >
            <div
              className="sd-original-column"
              hidden={compactReview && mobileReviewView !== "original"}
            >
              <OriginalDocument
                estateId={estateId}
                document={resultsDocument}
              />
            </div>
            <section
              className="sd-review-panel"
              hidden={compactReview && mobileReviewView !== "information"}
            >
              <Tabs
                items={[
                  {
                    key: "relevant",
                    label: `${
                      compactReview ? "Relevant" : "Relevant information"
                    } (${resultsDocument.proposals.length})`,
                    children: (
                      <>
                        {!resultsDocument.proposals.length ? (
                          <Empty description="No relevant estate or probate information was found. Inspect the original document alongside this panel." />
                        ) : (
                          <>
                            <div className="sd-record-toolbar">
                              <Select
                                aria-label="Record category"
                                value={group}
                                onChange={(value) =>
                                  navigate(() => {
                                    setGroup(value);
                                    setRecordId(
                                      resultsDocument.proposals.find(
                                        (p) =>
                                          value === "all" || p.target === value
                                      )?.id
                                    );
                                  })
                                }
                                options={[
                                  { value: "all", label: "All record types" },
                                  ...Array.from(
                                    new Set(
                                      resultsDocument.proposals.map(
                                        (p) => p.target
                                      )
                                    )
                                  ).map((target) => ({
                                    value: target,
                                    label: `${targetLabels[target]} (${
                                      resultsDocument.proposals.filter(
                                        (p) => p.target === target
                                      ).length
                                    })`,
                                  })),
                                ]}
                              />
                            </div>
                            <details className="sd-record-picker"><summary>Choose extracted record ({resultsDocument.proposals.length})</summary>
                            <div
                              className="sd-record-list"
                              aria-label="Extracted records"
                            >
                              {resultsDocument.proposals
                                .filter(
                                  (p) => group === "all" || p.target === group
                                )
                                .map((proposal) => (
                                  <button
                                    key={proposal.id}
                                    aria-pressed={
                                      selectedProposal?.id === proposal.id
                                    }
                                    onClick={() =>
                                      navigate(() => setRecordId(proposal.id))
                                    }
                                  >
                                    <span>
                                      <strong>{recordName(proposal)}</strong>
                                      <small>
                                        {targetLabels[proposal.target]}
                                      </small>
                                    </span>
                                    <Tag
                                      color={
                                        proposal.status === "confirmed"
                                          ? "green"
                                          : actionable(proposal)
                                          ? "gold"
                                          : "default"
                                      }
                                    >
                                      {proposal.status === "pending"
                                        ? "To review"
                                        : proposal.status === "unverified"
                                        ? "Saved for later"
                                        : proposal.status}
                                    </Tag>
                                  </button>
                                ))}
                            </div>
                            </details>
                            {selectedProposal && (
                              <div className="sd-selected-record">
                                <div className="sd-record-heading">
                                  <div>
                                    <h2>{recordName(selectedProposal)}</h2>
                                    <span>
                                      {targetLabels[selectedProposal.target]}
                                    </span>
                                  </div>
                                  {!editing &&
                                    canConfirm &&
                                    actionable(selectedProposal) && (
                                      <Button
                                        type="primary"
                                        onClick={startReview}
                                      >
                                        Review and confirm
                                      </Button>
                                    )}
                                </div>
                                {!canConfirm &&
                                  actionable(selectedProposal) && (
                                    <Alert
                                      type="info"
                                      message="Awaiting executor review"
                                      description="You can inspect the extracted values or save this information for later. An executor or administrator must confirm it."
                                    />
                                  )}
                                {editing ? (
                                  <>
                                    <div className="sd-destination">
                                      <label className="sd-field-label">
                                        Destination form
                                      </label>
                                      <Select
                                        disabled={busy}
                                        aria-label="Destination form"
                                        value={editing.proposal.target}
                                        options={[
                                          ...Object.keys(catalog.targets),
                                          "documentAnswers",
                                        ].map((target) => ({
                                          value: target,
                                          label: targetLabels[target] || target,
                                        }))}
                                        onChange={(target) => {
                                          const allowed =
                                            catalog.targets[target]?.map(
                                              (field) => field.key
                                            ) || [];
                                          setEditing({
                                            ...editing,
                                            proposal: {
                                              ...editing.proposal,
                                              target,
                                              recordUid: undefined,
                                              documentUid: undefined,
                                            },
                                          });
                                          setValuesText(
                                            JSON.stringify(
                                              Object.fromEntries(
                                                Object.entries(
                                                  editableValues
                                                ).filter(([key]) =>
                                                  allowed.includes(key)
                                                )
                                              ),
                                              null,
                                              2
                                            )
                                          );
                                          setConfirmed(false);
                                          setKeptFields([]);
                                        }}
                                      />
                                      <small>
                                        {[
                                          "estate",
                                          "owner",
                                          "estateTax",
                                          "documentAnswers",
                                        ].includes(editing.proposal.target) ||
                                        editing.proposal.recordUid
                                          ? "Update submitted fields in the selected existing record."
                                          : "Choose the destination record after reviewing suggested matches."}
                                      </small>
                                    </div>
                                    {comparison && (
                                      <Alert
                                        type={
                                          comparison.operation === "ambiguous"
                                            ? "warning"
                                            : "info"
                                        }
                                        message={
                                          {
                                            new: "New record",
                                            unchanged:
                                              "Existing record with no changes",
                                            update: "Proposed update",
                                            ambiguous: "Ambiguous match",
                                          }[comparison.operation]
                                        }
                                        description={
                                          comparison.operation === "ambiguous"
                                            ? "Choose an existing record or explicitly create a new record before saving. A shared name alone does not establish identity."
                                            : comparison.operation ===
                                              "unchanged"
                                            ? "The selected values match the saved record. Confirmation records your review without changing saved information."
                                            : comparison.operation === "update"
                                            ? "Compare the saved information with the extracted values below before confirming."
                                            : comparison.candidates.length
                                            ? "Possible existing records are shown below. Create a new record only if this is a distinct person, holding or transaction."
                                            : "Confirm that this information belongs in a new estate record."
                                        }
                                      />
                                    )}
                                    {editing.proposal.target === "creditor" &&
                                      comparison?.recordUid &&
                                      comparison.operation === "update" && (
                                        <Alert
                                          type="warning"
                                          message="Update this creditor in the creditors form, then return to compare and confirm. Existing creditor changes cannot be saved from this review."
                                        />
                                      )}
                                    {[
                                      "participant",
                                      "asset",
                                      "liability",
                                      "creditor",
                                      "cashflow",
                                    ].includes(editing.proposal.target) && (
                                      <div className="sd-destination">
                                        <label className="sd-field-label">
                                          Record to save
                                        </label>
                                        <Select
                                          disabled={busy || comparisonLoading}
                                          aria-label="Existing record"
                                          showSearch
                                          optionFilterProp="label"
                                          placeholder="Choose a record or create new"
                                          value={
                                            editing.proposal.recordUid === null
                                              ? "new"
                                              : editing.proposal.recordUid ||
                                                comparison?.recordUid
                                          }
                                          options={[
                                            {
                                              value: "new",
                                              label: "Create a new record",
                                            },
                                            ...(comparison?.records || []).map(
                                              (record) => ({
                                                value: record.uid,
                                                label: `Use ${record.label}`,
                                              })
                                            ),
                                          ]}
                                          onChange={(value) => {
                                            setEditing({
                                              ...editing,
                                              proposal: {
                                                ...editing.proposal,
                                                recordUid:
                                                  value === "new"
                                                    ? null
                                                    : value,
                                              },
                                            });
                                            setValuesText(
                                              JSON.stringify(
                                                {
                                                  ...editableValues,
                                                  ...Object.fromEntries(
                                                    keptFields.map((key) => [
                                                      key,
                                                      selectedProposal.values[
                                                        key
                                                      ],
                                                    ])
                                                  ),
                                                },
                                                null,
                                                2
                                              )
                                            );
                                            setKeptFields([]);
                                            setConfirmed(false);
                                          }}
                                        />
                                        {comparison?.candidates.map(
                                          (candidate) => (
                                            <p key={candidate.uid}>
                                              <strong>{candidate.label}</strong>
                                              : {candidate.reasons.join("; ")}
                                            </p>
                                          )
                                        )}
                                      </div>
                                    )}
                                    {editing.proposal.target ===
                                      "documentAnswers" && (
                                      <div className="sd-destination">
                                        <label className="sd-field-label">
                                          Court document
                                        </label>
                                        <Select
                                          disabled={busy}
                                          aria-label="Probate filing"
                                          placeholder="Select a court document"
                                          value={editing.proposal.documentUid}
                                          options={catalog.documents.map(
                                            (doc) => ({
                                              value: doc.uid,
                                              label: doc.name,
                                            })
                                          )}
                                          onChange={(documentUid) => {
                                            setEditing({
                                              ...editing,
                                              proposal: {
                                                ...editing.proposal,
                                                documentUid,
                                              },
                                            });
                                            setValuesText("{}");
                                            setConfirmed(false);
                                            setKeptFields([]);
                                          }}
                                        />
                                      </div>
                                    )}
                                    {comparisonLoading && (
                                      <div role="status">
                                        <Spin size="small" /> Loading current
                                        values...
                                      </div>
                                    )}
                                    {comparisonError && (
                                      <Alert
                                        type="warning"
                                        message={comparisonError}
                                      />
                                    )}
                                    <Button
                                      size="small"
                                      aria-label="Refresh saved values"
                                      icon={<ReloadOutlined />}
                                      disabled={busy || comparisonLoading}
                                      onClick={() => {
                                        setConfirmed(false);
                                        setComparisonRevision(
                                          (revision) => revision + 1
                                        );
                                      }}
                                    >
                                      Refresh saved values
                                    </Button>
                                    <fieldset
                                      disabled={busy}
                                      className="sd-fields"
                                    >
                                      {Object.entries({
                                        ...fieldValues,
                                        ...Object.fromEntries(
                                          keptFields.map((key) => [
                                            key,
                                            selectedProposal.values[key],
                                          ])
                                        ),
                                      }).map(([key, value]) => (
                                        <div key={key} className="sd-field">
                                          <label className="sd-field-label">
                                            {(
                                              reviewFields.find(
                                                (field) => field.key === key
                                              ) as any
                                            )?.label || fieldLabel(key)}
                                            {requiredFields.some(
                                              (field) => field.key === key
                                            ) && (
                                              <span className="sd-required">
                                                Required
                                              </span>
                                            )}
                                          </label>
                                          {comparison?.fields.find(
                                            (field) => field.key === key
                                          ) && (
                                            <Tag>
                                              {
                                                {
                                                  unchanged: "Unchanged",
                                                  fill: "Fill missing value",
                                                  replace:
                                                    "Replace saved value",
                                                }[
                                                  comparison.fields.find(
                                                    (field) => field.key === key
                                                  )!.change
                                                ]
                                              }
                                            </Tag>
                                          )}
                                          {currentValues[key] == null &&
                                            !comparison?.recordUid && (
                                              <small>
                                                Extracted:{" "}
                                                {displayValue(
                                                  selectedProposal.values[key]
                                                )}
                                              </small>
                                            )}
                                          {value === undefined && (
                                            <small>
                                              Not found in this document. Enter
                                              the required value.
                                            </small>
                                          )}
                                          {(comparison?.recordUid ||
                                            [
                                              "estate",
                                              "owner",
                                              "estateTax",
                                              "documentAnswers",
                                            ].includes(
                                              editing.proposal.target
                                            )) &&
                                            currentValues[key] !==
                                              undefined && (
                                              <details className="sd-comparison-details" open={comparison?.fields.find(field => field.key === key)?.change !== "unchanged"}>
                                                <summary>{comparison?.fields.find(field => field.key === key)?.change === "unchanged" ? "Saved and extracted values match" : "Compare saved and extracted values"}</summary>
                                              <div className="sd-current">
                                                <div className="sd-comparison-values">
                                                  <span>
                                                    <strong>Saved</strong>
                                                    {displayValue(
                                                      currentValues[key]
                                                    )}
                                                  </span>
                                                  <span>
                                                    <strong>Extracted</strong>
                                                    {displayValue(
                                                      selectedProposal.values[
                                                        key
                                                      ]
                                                    )}
                                                  </span>
                                                </div>
                                                <Button
                                                  size="small"
                                                  type="link"
                                                  onClick={() => {
                                                    if (
                                                      keptFields.includes(key)
                                                    ) {
                                                      setKeptFields((old) =>
                                                        old.filter(
                                                          (field) =>
                                                            field !== key
                                                        )
                                                      );
                                                      setField(
                                                        key,
                                                        selectedProposal.values[
                                                          key
                                                        ]
                                                      );
                                                    } else {
                                                      const next = {
                                                        ...editableValues,
                                                      };
                                                      delete next[key];
                                                      setValuesText(
                                                        JSON.stringify(
                                                          next,
                                                          null,
                                                          2
                                                        )
                                                      );
                                                      setKeptFields((old) => [
                                                        ...old,
                                                        key,
                                                      ]);
                                                      setConfirmed(false);
                                                    }
                                                  }}
                                                >
                                                  {keptFields.includes(key)
                                                    ? "Use extracted value"
                                                    : "Keep current value"}
                                                </Button>
                                              </div>
                                              </details>
                                            )}
                                          {keptFields.includes(key) ? (
                                            <Tag color="green">
                                              Keeping current value
                                            </Tag>
                                          ) : (
                                            <FieldInput
                                              disabled={busy}
                                              name={(reviewFields.find(field => field.key === key) as any)?.label || fieldLabel(key)}
                                              value={value}
                                              numeric={numericField(key)}
                                              date={(
                                                (
                                                  reviewFields.find(
                                                    (field) => field.key === key
                                                  ) as any
                                                )?.validators || []
                                              ).some(
                                                (v: any) =>
                                                  v.name === "isDateString"
                                              )}
                                              onChange={(next) =>
                                                setField(key, next)
                                              }
                                            />
                                          )}
                                        </div>
                                      ))}
                                    </fieldset>
                                    <Select<string>
                                      disabled={busy}
                                      aria-label="Add field"
                                      placeholder="Add another field"
                                      value={undefined}
                                      onChange={(key) => setField(key, "")}
                                      options={reviewFields
                                        .filter(
                                          (field) =>
                                            !(field.key in editableValues) &&
                                            !keptFields.includes(field.key)
                                        )
                                        .map((field) => ({
                                          value: field.key,
                                          label:
                                            (field as any).label ||
                                            fieldLabel(field.key),
                                        }))}
                                      style={{
                                        width: "100%",
                                        marginBottom: 16,
                                      }}
                                    />
                                    <Collapse
                                      items={[
                                        {
                                          key: "advanced",
                                          label: "Advanced field data",
                                          children: (
                                            <Input.TextArea
                                              disabled={busy}
                                              aria-label="Extracted field values"
                                              rows={10}
                                              value={valuesText}
                                              onChange={(event) => {
                                                setValuesText(
                                                  event.target.value
                                                );
                                                setConfirmed(false);
                                                setKeptFields([]);
                                              }}
                                            />
                                          ),
                                        },
                                      ]}
                                    />
                                  </>
                                ) : (
                                  <dl className="sd-value-list">
                                    {Object.entries(
                                      selectedProposal.values
                                    ).map(([key, value]) => (
                                      <div key={key}>
                                        <dt>{fieldLabel(key)}</dt>
                                        <dd>{displayValue(value)}</dd>
                                      </div>
                                    ))}
                                  </dl>
                                )}
                                <div className="sd-evidence">
                                  <span className="sd-eyebrow">
                                    SOURCE EVIDENCE
                                  </span>
                                  <p>
                                    {typeof selectedProposal.evidence ===
                                    "string"
                                      ? selectedProposal.evidence
                                      : JSON.stringify(
                                          selectedProposal.evidence ||
                                            "No source evidence supplied"
                                        )}
                                  </p>
                                  <small>
                                    AI-provided excerpt. Check it against the
                                    original document.
                                  </small>
                                </div>
                                {Boolean(selectedProposal.result) && (
                                  <Alert
                                    type={
                                      selectedProposal.status === "apply_failed"
                                        ? "warning"
                                        : "success"
                                    }
                                    message={
                                      selectedProposal.status === "confirmed"
                                        ? "Review complete. Confirmed information is saved to the estate record."
                                        : String(selectedProposal.result)
                                    }
                                  />
                                )}
                                {editError && (
                                  <Alert
                                    type="error"
                                    role="alert"
                                    message={editError}
                                  />
                                )}
                                {actionable(selectedProposal) && (
                                  <div className="sd-review-footer">
                                    {editing && (
                                      <Checkbox
                                        disabled={
                                          busy ||
                                          comparisonLoading ||
                                          !comparison ||
                                          comparison.operation === "ambiguous"
                                        }
                                        checked={confirmed}
                                        onChange={(event) =>
                                          setConfirmed(event.target.checked)
                                        }
                                      >
                                        I have reviewed these values and confirm
                                        they are correct.
                                      </Checkbox>
                                    )}
                                    <div>
                                      <Button
                                        disabled={
                                          busy ||
                                          ["unverified", "deferred"].includes(
                                            selectedProposal.status
                                          )
                                        }
                                        onClick={() =>
                                          navigate(() => {
                                            review(
                                              resultsDocument,
                                              selectedProposal,
                                              "defer"
                                            );
                                          })
                                        }
                                      >
                                        Save for later
                                      </Button>
                                      <Popconfirm
                                        title="Skip this extracted record? It will not be saved to estate forms."
                                        onConfirm={() =>
                                          navigate(() => {
                                            review(
                                              resultsDocument,
                                              selectedProposal,
                                              "reject"
                                            );
                                          })
                                        }
                                      >
                                        <Button disabled={busy}>
                                          Skip record
                                        </Button>
                                      </Popconfirm>
                                      {editing && (
                                        <Button
                                          type="primary"
                                          loading={busy}
                                          disabled={
                                            !confirmed ||
                                            !comparison ||
                                            comparison.operation ===
                                              "ambiguous" ||
                                            (editing.proposal.target ===
                                              "creditor" &&
                                              Boolean(comparison.recordUid) &&
                                              comparison.operation ===
                                                "update") ||
                                            (!Object.keys(editableValues)
                                              .length &&
                                              !comparison.recordUid &&
                                              ![
                                                "estate",
                                                "owner",
                                                "estateTax",
                                                "documentAnswers",
                                              ].includes(
                                                editing.proposal.target
                                              )) ||
                                            Boolean(comparisonError) ||
                                            comparisonLoading
                                          }
                                          onClick={confirm}
                                        >
                                          Confirm and save
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </>
                    ),
                  },
                  {
                    key: "raw",
                    label: "Extracted data",
                    children: (
                      <>
                        <Alert
                          type="info"
                          message="Structured AI extraction"
                          description="These are the saved extracted values and evidence, including any confirmed corrections. This is not a full document transcription."
                        />
                        <pre className="sd-raw">
                          {JSON.stringify(
                            {
                              documentType: resultsDocument.documentType,
                              proposals: resultsDocument.proposals.map(
                                ({ target, values, evidence, status }) => ({
                                  target,
                                  values,
                                  evidence,
                                  status,
                                })
                              ),
                            },
                            null,
                            2
                          )}
                        </pre>
                      </>
                    ),
                  },
                ]}
              />
            </section>
          </div>
        </>
      )}
      <Drawer
        open={uploadOpen}
        onClose={() => !uploading && setUploadOpen(false)}
        title="Upload documents"
        width={screens.sm === false ? "100%" : 480}
        destroyOnClose
      >
        <div className="sd-upload-context">
          <span className="sd-eyebrow">ACTIVE ESTATE</span>
          <p>
            {(selectedEstate as any)?.estateName ||
              (selectedEstate as any)?.name ||
              "Selected estate"}
          </p>
        </div>
        <Upload.Dragger
          className="sd-upload-dropzone"
          multiple
          maxCount={10}
          beforeUpload={() => false}
          fileList={files}
          onChange={({ fileList }) => setFiles(fileList)}
          accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.xlsm,.csv,.txt"
          disabled={uploading}
        >
          <UploadOutlined style={{ fontSize: 28 }} />
          <h3>Drop documents here</h3>
          <p>or click to browse files</p>
          <small>
            PDF, images, Word and spreadsheets. Up to 10 files, 20 MB each.
          </small>
        </Upload.Dragger>
        <div className="sd-upload-description">
          <label className="sd-field-label">Optional description</label>
          <p>
            Applies to all files in this upload. Leave blank to let AI identify
            the document type.
          </p>
          <Input.TextArea
            aria-label="Optional document description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            maxLength={2000}
            rows={3}
            placeholder="For example: death certificate and bank statements"
          />
        </div>
        <div className="sd-privacy-note">
          <SafetyOutlined />
          <span>
            Files stay private. Extraction sends content to OpenRouter and
            Google Vertex AI using our restricted privacy settings.
          </span>
        </div>
        <Button type="link" onClick={() => setPrivacyOpen(true)}>
          How document privacy works
        </Button>
        <Button
          block
          size="large"
          type="primary"
          loading={uploading}
          disabled={!files.length || uploading}
          onClick={async () => {
            setUploading(true);
            try {
              await service.upload(
                estateId,
                files.map((file) => file.originFileObj!).filter(Boolean),
                description
              );
              setFiles([]);
              setDescription("");
              await refresh();
              setUploadOpen(false);
            } catch (err) {
              setError(errorMessage(err));
            } finally {
              setUploading(false);
            }
          }}
        >
          Upload {files.length || ""} document{files.length === 1 ? "" : "s"}
        </Button>
        <p className="sd-upload-help">
          After uploading, select Extract information on a file. Extracted facts
          stay in review until an executor confirms them.
        </p>
      </Drawer>
      <Modal
        open={privacyOpen}
        title="Document privacy"
        onCancel={() => setPrivacyOpen(false)}
        footer={<Button onClick={() => setPrivacyOpen(false)}>Close</Button>}
      >
        {privacy}
      </Modal>
      <Modal
        open={Boolean(detailsUid)}
        title="Document description"
        onCancel={() => setDetailsUid(undefined)}
        onOk={() =>
          run(async () => {
            await service.describe(estateId, detailsUid!, detailsDescription);
            setDetailsUid(undefined);
          })
        }
        confirmLoading={busy}
      >
        <Input.TextArea
          aria-label="Document description"
          rows={4}
          maxLength={2000}
          value={detailsDescription}
          onChange={(event) => setDetailsDescription(event.target.value)}
        />
      </Modal>
    </div>
  );
}
